// Unresolved intervention flags panel for the BMD report.
//
// Read side only: resolving a flag is not wired yet. The richer reason types
// the design implies (FUNDAMENTAL_MISCONCEPTION_UNRESOLVED and
// SB_BELOW_PASS_THRESHOLD) arrive with the Phase 3 remediation runtime. Their
// rendering paths are reserved here so the UI keeps its shape when real data
// arrives.

import React from "react"
import type { BmdInterventionFlagRow } from "../types/bmd-report.types"
import { plainText } from "../utils/formatters"

type Severity = "urgent" | "review"

type FlagDisplay = {
  title: string
  description: string
  severity: Severity
}

// "Geometry" alone is useless when Geometry raised three flags, so lead with
// the skill and keep the topic as context.
function subject(row: BmdInterventionFlagRow): string {
  return row.skill_name
    ? `${plainText(row.skill_name)} (${plainText(row.topic_name)})`
    : plainText(row.topic_name)
}

function flagDisplay(row: BmdInterventionFlagRow): FlagDisplay {
  switch (row.reason) {
    // The first string in each group is what the backend actually writes to
    // dt_diagnostics_intervention_flag.reason; the second is the longer name
    // from the design doc. Both are matched because the panel once knew only
    // the design names, so real flags fell through to `default` and rendered
    // their raw reason string at the wrong severity. Keep the backend value
    // first: it is the source of truth.
    case "DOUBLE_TIMEOUT":
    case "CONSECUTIVE_TIMEOUTS":
      return {
        title: `${subject(row)}: ran out of time twice`,
        description:
          "The student ran out of time on the same question twice in a row. Check how they're coping before setting more diagnostics.",
        severity: "urgent",
      }
    case "FM_RETRY_FAILED":
    case "FUNDAMENTAL_MISCONCEPTION_UNRESOLVED":
      return {
        title: `${subject(row)}: still stuck after a second try`,
        description:
          "The student got a similar question wrong on their second attempt, so they need you to go through it with them. Nothing further will be set automatically.",
        severity: "urgent",
      }
    case "SB_BELOW_PASS_THRESHOLD":
      return {
        title: `${subject(row)}: Skill Builder score below 75%`,
        description:
          "The student didn't reach the pass mark. Go through the skill with them, then mark it done so they can try again.",
        severity: "review",
      }
    default:
      return {
        title: subject(row),
        description: "Needs a look from you.",
        severity: "review",
      }
  }
}

type SeverityStyle = {
  borderColor: string
  pillBg: string
  pillText: string
  pillLabel: string
}

function severityStyle(severity: Severity): SeverityStyle {
  if (severity === "urgent") {
    return {
      borderColor: "#dc3545",
      pillBg: "bg-danger bg-opacity-10",
      pillText: "text-danger",
      pillLabel: "Urgent",
    }
  }
  return {
    borderColor: "#fd7e14",
    pillBg: "bg-warning bg-opacity-25",
    pillText: "text-warning",
    pillLabel: "Needs review",
  }
}

export type BmdInterventionFlagsPanelProps = {
  flags: BmdInterventionFlagRow[]
}

export const BmdInterventionFlagsPanel: React.FC<
  BmdInterventionFlagsPanelProps
> = ({ flags }) => {
  return (
    <div className="bg-white rounded shadow-sm p-4 mb-4">
      <div className="text-uppercase text-muted font-size-12 mb-3 fw-bold">
        Needs Your Attention
      </div>
      {flags.length === 0 ? (
        <p className="text-muted mb-0 font-size-13">
          No active intervention flags.
        </p>
      ) : (
        <div className="d-flex flex-column gap-3">
          {flags.map((row) => {
            const d = flagDisplay(row)
            const style = severityStyle(d.severity)
            return (
              <div
                key={row.pk_intervention_key}
                className="ps-3"
                style={{ borderLeft: `3px solid ${style.borderColor}` }}
              >
                <div className="fw-semibold mb-1">{d.title}</div>
                <div className="text-muted font-size-13 mb-2">
                  {d.description}
                </div>
                <span
                  className={`badge ${style.pillBg} ${style.pillText} font-size-11`}
                >
                  {style.pillLabel}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default BmdInterventionFlagsPanel
